import { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { safeNext, withNext } from '../utils/safeNext';
import { useAuth } from '../hooks/useAuth';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { DWILogoIcon } from '../components/DWILogo';

const inputBase = {
  width: '100%', paddingTop: '14px', paddingBottom: '14px',
  paddingRight: '14px', paddingLeft: '46px', borderRadius: '12px',
  background: 'rgba(255,255,255,0.05)',
  border: '1px solid rgba(255,255,255,0.10)',
  color: '#F0F6FF', fontSize: '15px', outline: 'none',
  transition: 'border 0.15s, box-shadow 0.15s', boxSizing: 'border-box',
  fontFamily: 'Inter, system-ui, sans-serif',
};

export default function Login() {
  const { login }    = useAuth();
  const navigate     = useNavigate();
  const [params]     = useSearchParams();
  const [form, setForm]         = useState({ email: '', password: '' });
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [showPass, setShowPass] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(form.email, form.password);
      navigate(safeNext(params.get('next')));
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  const onFocus = e => {
    e.target.style.border    = '1px solid rgba(0,168,219,0.6)';
    e.target.style.boxShadow = '0 0 0 4px rgba(0,168,219,0.09)';
  };
  const onBlur = e => {
    e.target.style.border    = '1px solid rgba(255,255,255,0.10)';
    e.target.style.boxShadow = 'none';
  };

  return (
    <div style={{
      minHeight: '100vh', background: '#0A1628',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px 16px', position: 'relative', overflow: 'hidden',
      fontFamily: 'Inter, system-ui, sans-serif', boxSizing: 'border-box',
    }}>
      {/* Ambient glows */}
      <div aria-hidden style={{
        position: 'absolute', top: '-180px', left: '50%', transform: 'translateX(-50%)',
        width: '640px', height: '640px', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle,rgba(0,168,219,0.14) 0%,transparent 65%)',
      }} />
      <div aria-hidden style={{
        position: 'absolute', bottom: '-120px', right: '-60px',
        width: '480px', height: '480px', borderRadius: '50%', pointerEvents: 'none',
        background: 'radial-gradient(circle,rgba(27,97,172,0.12) 0%,transparent 65%)',
      }} />
      {/* Dot grid */}
      <div aria-hidden style={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        backgroundImage: 'radial-gradient(rgba(0,168,219,0.07) 1px,transparent 1px)',
        backgroundSize: '28px 28px',
      }} />

      <div style={{ width: '100%', maxWidth: '400px', position: 'relative', zIndex: 10 }}>

        {/* Brand mark */}
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <div style={{ position: 'relative', display: 'inline-block', marginBottom: '18px' }}>
            <div style={{
              position: 'absolute', inset: '-6px', borderRadius: '22px',
              background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
              filter: 'blur(16px)', opacity: 0.5,
            }} />
            <div style={{ position: 'relative' }}>
              <DWILogoIcon size={72} />
            </div>
          </div>
          <div style={{ fontSize: '28px', fontWeight: 900, letterSpacing: '-0.04em', lineHeight: 1.1, marginBottom: '8px' }}>
            <span style={{ color: '#F0F6FF' }}>DWI </span>
            <span style={{
              background: 'linear-gradient(135deg,#1B61AC,#00A8DB)',
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            }}>FINTECH</span>
          </div>
          <div style={{
            fontSize: '10px', fontWeight: 600, letterSpacing: '0.20em',
            textTransform: 'uppercase', color: 'rgba(240,246,255,0.28)',
          }}>
            Digital Finance Ecosystem
          </div>
        </div>

        {/* Card */}
        <div style={{
          background: 'rgba(13,28,53,0.92)',
          border: '1px solid rgba(0,168,219,0.15)',
          borderRadius: '20px', overflow: 'hidden',
          boxShadow: '0 0 0 1px rgba(255,255,255,0.04) inset,0 24px 80px rgba(0,0,0,0.60)',
        }}>
          {/* Accent line */}
          <div style={{ height: '2px', background: 'linear-gradient(90deg,transparent 0%,#1B61AC 30%,#00A8DB 70%,transparent 100%)' }} />

          <div style={{ padding: '28px 28px 32px' }}>
            <div style={{ marginBottom: '24px' }}>
              <h2 style={{ fontSize: '22px', fontWeight: 800, color: '#F0F6FF', margin: 0, letterSpacing: '-0.02em' }}>Welcome back</h2>
              <p style={{ color: 'rgba(240,246,255,0.38)', fontSize: '14px', marginTop: '4px', marginBottom: 0 }}>Sign in to your account</p>
            </div>

            {error && (
              <div style={{
                marginBottom: '20px', padding: '12px 16px', borderRadius: '12px',
                background: 'rgba(239,68,68,0.10)', border: '1px solid rgba(239,68,68,0.30)',
                color: '#f87171', fontSize: '14px',
              }}>{error}</div>
            )}

            <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '18px' }}>
              <div>
                <label style={{
                  display: 'block', fontSize: '11px', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.13em',
                  color: 'rgba(240,246,255,0.38)', marginBottom: '8px',
                }}>Email Address</label>
                <div style={{ position: 'relative' }}>
                  <Mail size={16} style={{
                    position: 'absolute', left: '15px', top: '50%',
                    transform: 'translateY(-50%)', color: 'rgba(240,246,255,0.28)', pointerEvents: 'none',
                  }} />
                  <input
                    type="email" placeholder="you@example.com"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    required autoComplete="email"
                    style={inputBase} onFocus={onFocus} onBlur={onBlur}
                  />
                </div>
              </div>

              <div>
                <label style={{
                  display: 'block', fontSize: '11px', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '0.13em',
                  color: 'rgba(240,246,255,0.38)', marginBottom: '8px',
                }}>Password</label>
                <div style={{ position: 'relative' }}>
                  <Lock size={16} style={{
                    position: 'absolute', left: '15px', top: '50%',
                    transform: 'translateY(-50%)', color: 'rgba(240,246,255,0.28)', pointerEvents: 'none',
                  }} />
                  <input
                    type={showPass ? 'text' : 'password'} placeholder="••••••••"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    required autoComplete="current-password"
                    style={{ ...inputBase, paddingRight: '48px' }}
                    onFocus={onFocus} onBlur={onBlur}
                  />
                  <button type="button" onClick={() => setShowPass(s => !s)}
                    aria-label="Toggle password visibility"
                    style={{
                      position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: 'rgba(240,246,255,0.30)', padding: 0,
                      display: 'flex', alignItems: 'center',
                    }}>
                    {showPass ? <EyeOff size={17} /> : <Eye size={17} />}
                  </button>
                </div>
                <div style={{ textAlign: 'right', marginTop: '8px' }}>
                  <Link to="/forgot-password"
                    style={{ color: 'rgba(240,246,255,0.45)', fontSize: '13px', textDecoration: 'none' }}
                    onMouseEnter={e => { e.currentTarget.style.color = '#00A8DB'; }}
                    onMouseLeave={e => { e.currentTarget.style.color = 'rgba(240,246,255,0.45)'; }}>
                    Forgot password?
                  </Link>
                </div>
              </div>

              <button type="submit" disabled={loading}
                style={{
                  width: '100%', marginTop: '6px', padding: '15px 0',
                  borderRadius: '12px', border: 'none',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  fontWeight: 800, fontSize: '16px', color: '#ffffff',
                  background: loading
                    ? 'rgba(0,168,219,0.40)'
                    : 'linear-gradient(135deg,#1B61AC 0%,#00A8DB 100%)',
                  boxShadow: loading ? 'none' : '0 6px 28px rgba(0,168,219,0.38)',
                  opacity: loading ? 0.75 : 1,
                  transition: 'box-shadow 0.2s,transform 0.15s,opacity 0.2s',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                  boxSizing: 'border-box', letterSpacing: '-0.01em',
                  fontFamily: 'Inter, system-ui, sans-serif',
                }}
                onMouseEnter={e => { if (!loading) { e.currentTarget.style.boxShadow='0 8px 36px rgba(0,168,219,0.55)'; e.currentTarget.style.transform='translateY(-1px)'; }}}
                onMouseLeave={e => { e.currentTarget.style.boxShadow='0 6px 28px rgba(0,168,219,0.38)'; e.currentTarget.style.transform='translateY(0)'; }}
              >
                {loading ? (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                      style={{ animation: 'dwi-spin 0.8s linear infinite' }}>
                      <circle cx="12" cy="12" r="10" stroke="rgba(255,255,255,0.25)" strokeWidth="3.5" />
                      <path d="M12 2a10 10 0 0 1 10 10" stroke="white" strokeWidth="3.5" strokeLinecap="round" />
                    </svg>
                    Signing in...
                  </>
                ) : 'Sign In'}
              </button>
            </form>

            <p style={{ textAlign: 'center', fontSize: '14px', color: 'rgba(240,246,255,0.38)', marginTop: '24px', marginBottom: 0 }}>
              No account?{' '}
              <Link to={withNext('/register', params.get('next'))}
                style={{ color: '#00A8DB', fontWeight: 700, textDecoration: 'none' }}
                onMouseEnter={e => { e.currentTarget.style.textDecoration='underline'; }}
                onMouseLeave={e => { e.currentTarget.style.textDecoration='none'; }}>
                Create one free
              </Link>
            </p>
          </div>
        </div>

        <p style={{ textAlign: 'center', fontSize: '11px', color: 'rgba(240,246,255,0.15)', marginTop: '20px' }}>
          © 2026 DWI Fintech · Digital Finance Ecosystem
        </p>
      </div>
    </div>
  );
}
